/**
 * Profile Link Service for Member Web
 * 
 * Provides consistent profile linking functionality that matches the mobile app implementation.
 * This ensures backend compatibility and consistent user experience across platforms.
 */

import { db } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  getDocs,
  collection,
  query,
  where,
  writeBatch,
  limit,
  Timestamp 
} from 'firebase/firestore';
import { MemberProfile } from '../types/auth';
import { 
  ValidationError, 
  NotFoundError, 
  logError, 
  retryWithBackoff, 
  getUserFriendlyMessage
} from '../utils/errorHandler';
import { currentOrgId, orgWhere, belongsToCurrentOrg } from '../utils/orgScope';

// Extended interface for internal use that includes fields from Firestore
interface InternalMemberProfile extends MemberProfile {
  cognitoUserId?: string;
}

export interface ProfileLink {
  id: string;
  cognitoUserId: string;
  memberId: string;
  firebaseUserId: string;
  linkedAt: Date;
  linkMethod: 'self_service' | 'admin_approval' | 'auto_link';
  verificationStatus: 'verified' | 'pending' | 'rejected';
  verificationAttempts: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProfileSearchQuery {
  name?: string;
  email?: string;
  memberNumber?: string;
  limit?: number;
}

export class ProfileLinkService {
  private static instance: ProfileLinkService;

  private constructor() {}

  public static getInstance(): ProfileLinkService {
    if (!ProfileLinkService.instance) {
      ProfileLinkService.instance = new ProfileLinkService();
    }
    return ProfileLinkService.instance;
  }

  /**
   * Create a profile link between Cognito user and member profile
   * This matches the mobile app implementation for backend compatibility
   */
  async createProfileLink(
    cognitoUserId: string,
    memberId: string,
    firebaseUserId?: string
  ): Promise<ProfileLink> {
    try {
      console.log('🔗 ProfileLinkService: Creating profile link:', {
        cognitoUserId,
        memberId,
      });

      // Validate inputs
      if (!cognitoUserId || cognitoUserId.trim() === '') {
        throw new ValidationError('Cognito user ID is required');
      }
      if (!memberId || memberId.trim() === '') {
        throw new ValidationError('Member ID is required');
      }

      // Use cognitoUserId as firebaseUserId if not provided (member web pattern)
      const effectiveFirebaseUserId = firebaseUserId || cognitoUserId;

      // Check if Cognito user already has a link
      const existingUserLink = await this.getProfileLink(cognitoUserId);
      if (existingUserLink) {
        console.error('❌ ProfileLinkService: User already has a profile link');
        throw new ValidationError('Your account is already linked to a profile');
      }

      // Check if member profile is already linked
      const existingMemberLink = await this.getProfileLinkByMemberId(memberId);
      if (existingMemberLink) {
        console.error('❌ ProfileLinkService: Member profile already linked');
        throw new ValidationError('This profile is already linked to another account');
      }

      // Verify member profile exists
      const memberRef = doc(db, 'members', memberId);
      const memberSnap = await getDoc(memberRef);
      if (!memberSnap.exists() || !belongsToCurrentOrg(memberSnap.data())) {
        console.error('❌ ProfileLinkService: Member profile not found');
        throw new NotFoundError('Member profile not found');
      }

      return await retryWithBackoff(async () => {
        // Use batch write for atomicity
        const batch = writeBatch(db);
        const now = Timestamp.now();

        // Create profile link document using firebaseUserId as document ID
        // This is critical for backend lookups: profileLinks.doc(firebaseUserId).get()
        const profileLinkRef = doc(db, 'profileLinks', effectiveFirebaseUserId);
        const profileLinkData = {
          cognitoUserId,
          memberId,
          memberDocId: memberId, // Backend expects this field
          firebaseUserId: effectiveFirebaseUserId,
          userId: effectiveFirebaseUserId, // Also set userId for backward compatibility
          organizationId: currentOrgId(),
          linkedAt: now,
          linkMethod: 'self_service',
          verificationStatus: 'verified',
          verificationAttempts: 0,
          metadata: {
            platform: 'member_web',
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          },
          createdAt: now,
          updatedAt: now,
        };

        batch.set(profileLinkRef, profileLinkData);

        // Update member document with link information
        const memberUpdateData = {
          cognitoUserId,
          firebaseUserId: effectiveFirebaseUserId, // Critical for PaymentService lookups
          firebaseUid: effectiveFirebaseUserId, // Keep for backward compatibility
          linkedAt: now,
          lastLoginAt: now,
          updatedAt: now,
        };
        batch.update(memberRef, memberUpdateData);

        // Commit the batch
        await batch.commit();

        console.log('✅ ProfileLinkService: Profile link created:', profileLinkRef.id);

        // Return the created profile link
        return {
          id: profileLinkRef.id,
          cognitoUserId,
          memberId,
          firebaseUserId: effectiveFirebaseUserId,
          linkedAt: now.toDate(),
          linkMethod: 'self_service' as const,
          verificationStatus: 'verified' as const,
          verificationAttempts: 0,
          createdAt: now.toDate(),
          updatedAt: now.toDate(),
        };
      });
    } catch (error) {
      logError(error, 'createProfileLink');
      throw error instanceof ValidationError || error instanceof NotFoundError
        ? error
        : new Error(getUserFriendlyMessage(error));
    }
  }

  /**
   * Get profile link for a Cognito user
   */
  async getProfileLink(cognitoUserId: string): Promise<ProfileLink | null> {
    try {
      console.log('🔍 ProfileLinkService: Getting profile link for:', cognitoUserId);

      if (!cognitoUserId || cognitoUserId.trim() === '') {
        throw new ValidationError('Cognito user ID is required');
      }

      // First, try to find by firebaseUserId (document ID)
      const profileLinkRef = doc(db, 'profileLinks', cognitoUserId);
      const profileLinkSnap = await getDoc(profileLinkRef);

      if (profileLinkSnap.exists() && belongsToCurrentOrg(profileLinkSnap.data())) {
        const data = profileLinkSnap.data();
        return this.mapFirestoreDocToProfileLink(profileLinkSnap.id, data);
      }

      // Fallback: search by cognitoUserId field
      const profileLinksRef = collection(db, 'profileLinks');
      const q = query(profileLinksRef, where('cognitoUserId', '==', cognitoUserId), orgWhere());
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return this.mapFirestoreDocToProfileLink(doc.id, data);
      }

      console.log('⚠️ ProfileLinkService: No profile link found for:', cognitoUserId);
      return null;
    } catch (error: any) {
      // New users have no profileLink yet; Firestore rules reject the read with
      // permission-denied. Treat that as "no link yet" (return null) so sign-in
      // proceeds to the profile-claim flow instead of erroring.
      const code = error?.code;
      if (
        code === 'permission-denied' ||
        code === 'firestore/permission-denied' ||
        code === 'unauthenticated' ||
        code === 'not-found'
      ) {
        console.warn('getProfileLink: no accessible link yet, treating as null:', code);
        return null;
      }
      logError(error, 'getProfileLink');
      throw new Error(getUserFriendlyMessage(error));
    }
  }

  /**
   * Search for member profiles by name, email, or member number
   */
  async searchMemberProfiles(searchQuery: ProfileSearchQuery): Promise<MemberProfile[]> {
    try {
      console.log('🔍 ProfileLinkService: Searching member profiles:', searchQuery);

      const limitCount = searchQuery.limit || 50;
      const results: InternalMemberProfile[] = [];
      const seenIds = new Set<string>();

      // Search by member number (exact match)
      if (searchQuery.memberNumber) {
        console.log('🔍 Searching by member number:', searchQuery.memberNumber);
        const membersRef = collection(db, 'members');
        const memberNumberQuery = query(
          membersRef,
          where('memberNumber', '==', searchQuery.memberNumber),
          orgWhere(),
          limit(limitCount)
        );
        const snapshot = await getDocs(memberNumberQuery);

        snapshot.docs.forEach(doc => {
          const member = this.mapFirestoreDocToMember(doc);
          if (!seenIds.has(member.id)) {
            results.push(member);
            seenIds.add(member.id);
          }
        });
      }

      // Search by email (exact match)
      if (searchQuery.email && results.length < limitCount) {
        console.log('🔍 Searching by email:', searchQuery.email);
        const membersRef = collection(db, 'members');
        const emailQuery = query(
          membersRef,
          where('email', '==', searchQuery.email.toLowerCase()),
          orgWhere(),
          limit(limitCount)
        );
        const snapshot = await getDocs(emailQuery);

        snapshot.docs.forEach(doc => {
          const member = this.mapFirestoreDocToMember(doc);
          if (!seenIds.has(member.id)) {
            results.push(member);
            seenIds.add(member.id);
          }
        });
      }

      // Search by name (prefix match on displayName, firstName, lastName)
      if (searchQuery.name && results.length < limitCount) {
        console.log('🔍 Searching by name:', searchQuery.name);
        const searchTerm = searchQuery.name.toLowerCase().trim();
        const membersRef = collection(db, 'members');

        // Search by displayName prefix
        const displayNameQuery = query(
          membersRef,
          where('displayName', '>=', searchTerm),
          where('displayName', '<=', searchTerm + '\uf8ff'),
          orgWhere(),
          limit(limitCount)
        );
        const displayNameSnapshot = await getDocs(displayNameQuery);

        displayNameSnapshot.docs.forEach(doc => {
          const member = this.mapFirestoreDocToMember(doc);
          if (!seenIds.has(member.id)) {
            results.push(member);
            seenIds.add(member.id);
          }
        });

        // Search by lastName prefix if we need more results
        if (results.length < limitCount) {
          const lastNameQuery = query(
            membersRef,
            where('lastName', '>=', searchTerm),
            where('lastName', '<=', searchTerm + '\uf8ff'),
            orgWhere(),
            limit(limitCount)
          );
          const lastNameSnapshot = await getDocs(lastNameQuery);

          lastNameSnapshot.docs.forEach(doc => {
            const member = this.mapFirestoreDocToMember(doc);
            if (!seenIds.has(member.id)) {
              results.push(member);
              seenIds.add(member.id);
            }
          });
        }
      }

      // Filter out profiles that are already linked (don't have cognitoUserId)
      const unlinkedResults = results.filter(member => !member.cognitoUserId);

      console.log(
        `✅ ProfileLinkService: Found ${unlinkedResults.length} unlinked profiles`
      );
      // Cast to MemberProfile to remove internal fields from return type
      return unlinkedResults.slice(0, limitCount) as MemberProfile[];
    } catch (error) {
      logError(error, 'searchMemberProfiles');
      throw new Error(getUserFriendlyMessage(error));
    }
  }

  /**
   * Find member profiles by email
   */
  async findMemberByEmail(email: string): Promise<MemberProfile[]> {
    try {
      console.log('🔍 ProfileLinkService: Finding member by email:', email);

      if (!email || email.trim() === '') {
        throw new ValidationError('Email is required');
      }

      const normalizedEmail = email.toLowerCase().trim();
      const membersRef = collection(db, 'members');
      const emailQuery = query(membersRef, where('email', '==', normalizedEmail), orgWhere());
      const snapshot = await getDocs(emailQuery);

      const members = snapshot.docs
        .map(doc => this.mapFirestoreDocToMember(doc))
        .filter(member => !(member as InternalMemberProfile).cognitoUserId); // Only return unlinked profiles

      console.log(`✅ ProfileLinkService: Found ${members.length} unlinked members with email`);
      // Cast to MemberProfile to remove internal fields from return type
      return members as MemberProfile[];
    } catch (error) {
      logError(error, 'findMemberByEmail');
      throw new Error(getUserFriendlyMessage(error));
    }
  }

  /**
   * Get profile link by member ID (internal helper)
   */
  private async getProfileLinkByMemberId(memberId: string): Promise<ProfileLink | null> {
    try {
      const profileLinksRef = collection(db, 'profileLinks');
      const q = query(profileLinksRef, where('memberId', '==', memberId), orgWhere());
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      return this.mapFirestoreDocToProfileLink(doc.id, data);
    } catch (error) {
      logError(error, 'getProfileLinkByMemberId');
      return null;
    }
  }

  /**
   * Map Firestore document to ProfileLink
   */
  private mapFirestoreDocToProfileLink(id: string, data: any): ProfileLink {
    return {
      id,
      cognitoUserId: data.cognitoUserId || '',
      memberId: data.memberId || data.memberDocId || '',
      firebaseUserId: data.firebaseUserId || data.userId || '',
      linkedAt: data.linkedAt?.toDate() || new Date(),
      linkMethod: data.linkMethod || 'self_service',
      verificationStatus: data.verificationStatus || 'verified',
      verificationAttempts: data.verificationAttempts || 0,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }

  /**
   * Map Firestore document to MemberProfile (with internal fields)
   */
  private mapFirestoreDocToMember(doc: any): InternalMemberProfile {
    const data = doc.data();
    return {
      id: doc.id,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      displayName: data.displayName || 
                   data.fullName || 
                   `${data.firstName || ''} ${data.lastName || ''}`.trim() ||
                   data.email?.split('@')[0] || 
                   'Member',
      email: data.email || '',
      phone: data.phone || '',
      profileImageUrl: data.profileImageUrl || 
                      data.photoUrl || 
                      data.profilePicture || 
                      data.avatar || 
                      '',
      initiationDate: data.initiationDate?.toDate(),
      status: data.status || 'active',
      allowDirectMessages: data.allowDirectMessages ?? true,
      hiddenThreads: data.hiddenThreads || [],
      entityMemberships: data.entityMemberships || [],
      cognitoUserId: data.cognitoUserId, // Include for filtering
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }
}

// Export singleton instance
export const profileLinkService = ProfileLinkService.getInstance();
export default profileLinkService;